import {
  ActivityType,
  activityLabel,
  INote,
  NoteSyncStatus,
} from "shared";
import { Tooltip } from "@mui/material";
import { When } from "react-if";
import {
  ChatBubbleLeftRightIcon,
  CloudArrowUpIcon,
  EnvelopeIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";
import { useParams } from "react-router";
import { useMemo } from "react";

import { Paragraph } from "../../../common/Atoms/Typography/Paragraph";
import { classNames } from "../../../common/lib/classNames";
import { WrappedLink } from "../WrappedLink";
import { useAccountingSystemName } from "../../hooks/useAccountingSystemName";

import { NoteTimestamp } from "./NoteTimestamp";
import { NoteAttachments } from "./NoteAttachments";
import { CallTranscript } from "./CallTranscript";

interface NoteProps {
  note: INote;
}

export function Note({ note }: NoteProps) {
  const name = `${note.userFirstName || ``} ${note.userLastName || ``}`.trim();
  const accountingName = useAccountingSystemName();
  const { escalationId: escalationIdParam } = useParams();

  const hasFooter = useMemo(() => {
    if (note.escalationId && !escalationIdParam) {
      return true;
    }

    if (escalationIdParam && note.escalationId === Number(escalationIdParam)) {
      return true;
    }

    return false;
  }, [note.escalationId, escalationIdParam]);

  const highlight = escalationIdParam && note.escalationId === Number(escalationIdParam);

  const activityText = note.activity ? activityLabel(note.activity.type, note.activity.callOutcome) : null;
  const ActivityIcon = note.activity?.type === ActivityType.TEXT_MESSAGE ? ChatBubbleLeftRightIcon
    : note.activity?.type === ActivityType.EXTERNAL_EMAIL ? EnvelopeIcon
      : PhoneIcon;

  const header = name ?
    (
      <Paragraph
        variant={ `secondary` }
      >
        <Paragraph
          as={ `span` }
          className={ `font-semibold` }
        >
          { name }
        </Paragraph>
        <span>
          { note.activity ? ` logged a ${activityText}` : ` created a note` }
        </span>

        <When condition={ note.invoiceNumber && note.invoiceId }>
          <WrappedLink
            to={ `/invoices/${note.invoiceId}` }
          >
            { ` on invoice ` }
            <Paragraph
              as={ `span` }
              variant={ `link` }
            >
              <span className={ `font-semibold` }>
                { note.invoiceNumber }
              </span>
            </Paragraph>
          </WrappedLink>
        </When>
      </Paragraph>
    ) : <div />;

  return (
    <div
      id={ `note-${note.id}` }
      className={ classNames(
        note.content ? `border border-gray-100 rounded-md p-3`: ``,
        highlight ? `bg-yellow-50` : ``,
      ) }>
      <div className={ `flex justify-between` }>
        { /* The name of the user if applicable */ }
        <div className={ `flex` }>
          <When condition={ !!note.activity }>
            <ActivityIcon className={ `h-5 w-5 text-slate-500 mr-1` } />
          </When>

          { header }

          <When condition={ note.syncStatus === NoteSyncStatus.NOT_SYNCED || note.syncStatus === NoteSyncStatus.DEFFERED }>
            <Tooltip title={ `Note not yet synced with ${accountingName}` }>
              <CloudArrowUpIcon className={
                classNames(
                  `h-5 w-5 text-orange-400`,
                  header && `ml-1`,
                )
              } />
            </Tooltip>
          </When>

          <When condition={ note.syncStatus === NoteSyncStatus.SYNCED }>
            <Tooltip title={ `Synced with ${accountingName}` }>
              <CloudArrowUpIcon className={
                classNames(
                  `h-5 w-5 text-green-400`,
                  header && `ml-1`,
                )
              } />
            </Tooltip>
          </When>

          <When condition={ note.syncStatus === NoteSyncStatus.ERROR }>
            <Tooltip title={ note.syncError || `Unknown Sync Error` }>
              <CloudArrowUpIcon className={
                classNames(
                  `h-5 w-5 text-red-600`,
                  header && `ml-1`,
                )
              } />
            </Tooltip>
          </When>
        </div>

        { /* When it was left (activity notes show when the activity happened) */ }
        <NoteTimestamp date={ note.activity?.activityTime || note.createdAt } />
      </div>
      { /* The main content */ }
      <Paragraph
        variant={ `note` }
        className={ `mt-2 whitespace-pre-wrap` }
      >
        { note.content }
      </Paragraph>

      <NoteAttachments attachments={ note.attachments } />

      { note.activity?.contactCallId && (
        <CallTranscript
          organisationId={ note.organisationId }
          contactId={ note.contactId }
          contactCallId={ note.activity.contactCallId }
          callerName={ note.userFirstName }
        />
      ) }

      { /* The footer, shows the type of note etc */ }
      <When condition={ hasFooter }>
        <div className={ `border-t border-gray-50 mt-3` }/>
        <div className={ `pt-3` }>
          { /* Was this created via an escalation? */ }
          <When condition={ note.escalationId && !escalationIdParam }>
            <Paragraph
              variant={ `help` }
              size={ `xs` }
            >
              { `Note was created from an escalation` }
            </Paragraph>
          </When>

          <When condition={ escalationIdParam && note.escalationId === Number(escalationIdParam) }>
            <Paragraph
              variant={ `help` }
              size={ `xs` }
            >
              { `Note was created in this escalation` }
            </Paragraph>
          </When>
        </div>
      </When>
    </div>
  );
}
